import React, { useEffect, useState, useRef } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, Scatter, ScatterChart, ComposedChart, ResponsiveContainer, ReferenceLine, CartesianGrid } from "recharts";
import axios from 'axios';
import "./Graph.css";
import {getGameInfo, extractRelevantFieldsByElement, HORIZONTALCOORDS} from './App'
import {parseCompressedVertices} from '@sabaki/sgf'
import server from "./Server"
import MoveEvaluator from "./MoveEvaluator";
import { data } from "react-router-dom";
const sha256 = require("js-sha256");

const _ = require('lodash');

export const treeToJSON = (tree,currents={},nodeId=null,alreadyAnalyzed=new Set()) => {

  const boardDimensions = getGameInfo(tree,currents).size
  
  // Compute total number of moves
  const nodes = nodeId ? [...tree.listNodesVertically(nodeId,-1,{})].reverse() : [...tree.listCurrentNodes(currents)]

  // Convert to GTP format for Katago engine
  let moves = []

  // Check for initial/handicap stones 
  let initialStones = []
  let initialMoves = []
  let initialPlayer = 'B'
  let rootNode = tree.root

  for (let prop of ['B', 'W', 'AB', 'AW']) {
    if (rootNode.data[prop] == null)
      continue
    let color = prop.slice(-1)
    let sign = color === 'B' ? 1 : -1
    let vertices = [].concat(
      ...rootNode.data[prop].map(parseCompressedVertices)
    )

    for (let vertex of vertices) {
      // Convert [2,4] -> "C15" for Katago 
      const gtpVertex=HORIZONTALCOORDS[vertex[0]]+String(boardDimensions[0] - vertex[1])
      const move = [color,gtpVertex]
      if (prop[0] === 'A') {
        initialStones.push(move)
        initialMoves.push([sign,vertex])
      }
      else {
        moves.push(move)  
      }
    }
  }

  if (initialStones.length > 0) {

    if (initialStones[initialStones.length-1][0] === 'B') {
      initialPlayer = 'W';
    }

  }

  for (let node of nodes) {

    for (let prop of ['B', 'W']) {
      let color = prop.slice(-1)
      if (node.data[prop] == undefined) { // wrong prop
        continue
      } else if (node.data[prop] == '') { // pass
        const move = [color,'pass']
        moves.push(move)  
        continue
      } else {
        let sign = color === 'B' ? 1 : -1
        let vertices = [].concat(
          ...node.data[prop].map(parseCompressedVertices)
        )

        for (let vertex of vertices) {
          // Convert [2,4] -> "C15" for Katago 
          const gtpVertex=HORIZONTALCOORDS[vertex[0]]+String(boardDimensions[0] - vertex[1])
          const move = [color,gtpVertex]
          moves.push(move)  
        }
      }
    }
  }

  // Note that nodes contains the root node which has no move info, 
  // just game info. 
  const totalNumberOfMoves = nodes.length 

  // by default, we ask Katago to analyze all moves
  const allMoves = [...Array(totalNumberOfMoves).keys()];
  const analyzeTurns = _.difference(allMoves, alreadyAnalyzed);
  const onlyAnalyzeLastMove = nodeId !== null;
  const gameInfo = getGameInfo(tree,currents);
  const dimensions = gameInfo.size;
  const moveId = onlyAnalyzeLastMove ? sha256(JSON.stringify(moves)) : null; 

  let treeInJSON = {
    gameId: gameInfo.gameId,
    ...(onlyAnalyzeLastMove && {moveId: moveId}), 
    katagoRequest: {
      moves: moves,
      rules: 'tromp-taylor',
      komi: gameInfo.komi,
      boardXSize: dimensions[0],
      boardYSize: dimensions[1],
      ...(!onlyAnalyzeLastMove && {analyzeTurns: analyzeTurns}),
      ...(initialStones.length > 0 && {initialStones: initialStones}),
      ...(initialPlayer && {initialPlayer: initialPlayer}),
    }
  };

  return treeInJSON
}

const sampleKatagoRequestInJSON = {
  id: 'foo',
  initialStones: [['B', 'Q4'], ['B', 'C4']],
  moves: [['W', 'P5'], ['B', 'P6']],
  rules: 'tromp-taylor',
  komi: 6.5,
  boardXSize: 19,
  boardYSize: 19,
  analyzeTurns: [0, 1, 2],
};

const Graph = ({ gameId, state, gameLength, currentMoveNumber, redrawThenGoToMove, 
                 analyzedMoves, setAnalyzedMoves, analyzedMovesRef, 
                 analysisComplete, setAnalysisComplete, analysisCompleteRef, 
                 preferences }) => {

  const [error, setError] = useState(null);
  const [waitingOnKatago,setWaitingOnKatago] = useState(false)
  const [currentlyFetching, setCurrentlyFetching] = useState(false);
  const [requestedMoves, setRequestedMoves] = useState([])
  const [xAxisData, setXAxisData] = useState({ticks: [], min: 0, max: 0})
  const [moveData, setMoveData] = useState(undefined)
  const [blunders, setBlunders] = useState(undefined)
    
  // To ensure current values in useEffects below, we use useRef 
  // for a bunch of variables. This seems so hacky, is this really 
  // the best practice? 
  const waitingOnKatagoRef = useRef(waitingOnKatago); 
  const gameLengthRef = useRef(gameLength);
  const requestedMovesToBeAnalyzedRef = useRef(requestedMoves);

  useEffect(() => {
    // Update the ref whenever the state changes
    waitingOnKatagoRef.current = waitingOnKatago;
  }, [waitingOnKatago]);

  useEffect(() => {
    gameLengthRef.current = gameLength;
  }, [gameLength]);

  useEffect(() => {
    requestedMovesToBeAnalyzedRef.current = requestedMoves;
    }, [requestedMoves]);

  // Recalculate ticks on graph
  useEffect(() => {
    const scores = gameId in analyzedMoves 
            ? analyzedMoves[gameId].map(x => -extractRelevantFieldsByElement(x,analyzedMoves[gameId]).score) 
            : undefined

    if(scores === undefined)
      return

    const minScore = Math.min(...scores);
    const maxScore = Math.max(...scores);

    // Round minScore down to nearest multiple of 5
    const roundedMinScore = Math.floor(minScore / 5) * 5;
    // Round maxScore up to nearest multiple of 5
    const roundedMaxScore = Math.ceil(maxScore / 5) * 5;

    const step = 5; 
    // Generate ticks based on min and max
    const Xticks = [];
    for (let val = Math.floor(roundedMinScore); val <= Math.ceil(roundedMaxScore); val += step) {
      Xticks.push(val);
      if(val < 0 && val+step > 0) {
        Xticks.push(0)
      }
    }
    setXAxisData({ticks: Xticks, min: roundedMinScore, max: roundedMaxScore})

    // Calculate blunders
    let updatedMoveData = analyzedMoves[gameId]?.sort(x => x.turn).map(x => extractRelevantFieldsByElement(x,analyzedMoves[gameId]))
    updatedMoveData = updatedMoveData?.map((b) => ({
      ...b, 
      score: -b.score, // Flip the sign so that the plot shows black on the left and white on the right
    }))
    setMoveData(updatedMoveData.sort(x => x.turn))

    const moveEvaluator = new MoveEvaluator(preferences.evaluationThresholds)
    
    // Identify blunders
    const updatedBlunders = updatedMoveData?.filter((move) => moveEvaluator(move) === 'blunder')?.map((b) => ({
      ...b,
      turn: b.turn - 1, // Offset by 1 so blunders are w.r.t. next turn not current turn
      moveScore: b.currentPlayer === 'B' ? b.moveDelta : - b.moveDelta, 
      side: b.currentPlayer === 'B' ? // White's blunder
      Math.min(roundedMinScore,0) : 
      Math.max(roundedMaxScore,0) , 
    }));

    // console.log("Updated blunders:",updatedBlunders)

    setBlunders(updatedBlunders)

  }, [analyzedMoves, preferences])

  useEffect(() => {

    // Only post new moves if there's a game loaded and we're not 
    // already waiting on moves to be computed
    if (!currentlyFetching && state.gameTrees !== undefined 
      && !waitingOnKatagoRef.current
      && !! preferences.depth ) {
        let {gameTrees, gameIndex, gameCurrents} = state
        let tree = gameTrees[gameIndex]
        let currents = gameCurrents[gameIndex]
        server.data.depth = preferences.depth 
        server.initialFetch(tree,currents,preferences.depth); 

      } else {
        if (state.gameTrees === undefined) {
          setError('No game uploaded.')
          console.log('No game uploaded.')
      } else if (! !! preferences.depth) {
        console.log('Search depth not specified in preferences.')
      }
    }
  }, [state.gameTrees, preferences]);
  

  // This handles a click on the graph, creating a reference line on the selected move number 
  const handleGraphClickAndCreateReferenceLine = (dataPoint) => {
    if (dataPoint && dataPoint.activePayload && dataPoint.activePayload[0]) {
      const clickedMoveNumber = dataPoint.activePayload[0].payload.turn;
      redrawThenGoToMove(clickedMoveNumber);
    }
  };

  const handleBlunderClick = (dataPoint, event) => {
    event.stopPropagation();
    if (dataPoint) {
      redrawThenGoToMove(dataPoint.turn);
    }
  };

  const roundToNearestHalf = (num) => {
    return Math.round(num * 2) / 2;
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const x = -roundToNearestHalf(payload[0].payload.score)
      const roundedScore = x < 0 ? "W+"+Math.abs(x) : 
            (x > 0 ? "B+"+Math.abs(x) : "0")
      return (
        <div className="custom-tooltip" style="display: flex; justify-content: space-between; align-items: center; width: 100%">
          <p style="font-size: 14px; text-align: left;">{`Move ${label}`}</p>
          <p style="font-size: 14px; text-align: right">{`Score ${roundedScore}`}</p>
          {/* <p className="intro">{getIntroOfPage(label)}</p> */}
          {/* <p className="desc">Anything you want can be displayed here.</p> */}
        </div>
      );
    }
  
    return null;
  };

  return (
    <ResponsiveContainer width="100%">
      <ComposedChart
        width="100%"
        layout="vertical"
        onClick={handleGraphClickAndCreateReferenceLine}
        margin={{ top: 40, right: 30, bottom: 5, left: 30 }} // Adjust bottom padding
        data={moveData}
      >
        {/* <XAxis
          type="number"
          domain={[0, 100]} // Winrate as percentage
          label={{ value: 'Black Win %', position: 'insideBottom', offset: -10 }}
        /> */}
        <XAxis
          type="number"
          data="moveScore"
          domain={[xAxisData.min,xAxisData.max]}
          // label={{ value: 'Score', position: 'insideBottom', offset: -20 }}
          ticks= {xAxisData.ticks}
          interval="preserveStartEnd" // To ensure endpoint ticks are shown
          tickFormatter={(x) => x === xAxisData.min ? "B+"+Math.abs(x) : 
            x < 0 ? Math.abs(x) : 
            (x > 0 ? (x === xAxisData.max ? "W+"+Math.abs(x) : Math.abs(x)) 
            : "0")}
          style={{
            fontSize: '12px',
        }}
        />
        <YAxis
          type="number"
          dataKey="turn"
          axisLine={false}
          yAxisId={0}
          tickFormatter={() => ''}
          domain={[0, gameLength]}
          allowDataOverflow={true}
          hide
          />
        <YAxis
          type="number"
          dataKey="turn"
          axisLine={false}
          yAxisId={1}
          tickFormatter={() => ''}
          domain={[0, gameLength]}
          allowDataOverflow={true}
          hide
          />
         <defs>
          <linearGradient id="fillGradient" x1="-1" y1="0" x2="1" y2="0">
            <stop offset="5%" stopColor="#083054" stopOpacity={1} />
            <stop offset="95%" stopColor="#FFFFFF" stopOpacity={1} />
          </linearGradient>
        </defs>
        {/* <Area
          type="monotone"
          dataKey="blackWinrate"
          stroke="#000"
          dot={false}
          fill="url(#splitColor)"
        /> */}
        {/* <CartesianGrid /> */}
        <Area type="monotone" dataKey="score" 
              stackId="1"       
              fill="url(#fillGradient)"
              data={moveData}
              syncId='chart'
              yAxisId={0}
              isAnimationActive={false}
              />
        {currentMoveNumber && (
          <ReferenceLine
            y={currentMoveNumber}
            stroke="#083054"
            strokeWidth={0.5}
            ifOverflow="visible"
            label={{
              value: `${currentMoveNumber}`,
              position: "insideRight",
              dx: 27, // Add extra horizontal offset for better visibility
              fontSize: 12, // Adjust text size
            }}
          />
        )}
        {analyzedMoves && (<ReferenceLine
            x={0}
            stroke="black"
            strokeWidth={0.1}
            ifOverflow="visible"
            strokeDasharray="3 3"
          /> 
        )}
        <Tooltip 
          content={<CustomTooltip />} 
          position={{ x: 0, y: -15 }}
          />
        {/* Scatter Points for Blunders */}
        {blunders?.length > 0 && <Scatter
          data={blunders}
          dataKey="side"
          fill="red"
          onClick={(datapoint, index, event) => handleBlunderClick(datapoint, event)}
          syncId='chart'
          yAxisId={1}
          isAnimationActive={false}
          shape={(props) => (
            <circle
              {...props}
              className="blunder-circle"
              r={3} 
              stroke="red"
              strokeWidth={2}
              fill="red"
              cursor="pointer"
              />
          )}
        />}
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default Graph;
